// Drawola Studio — the per-card "⋮" dropdown. Fork-only.
//
// Why a portal
// ------------
// Both card containers clip their content: `.drawola-studio__card` (grid) and
// `.drawola-studio__list` (list) set `overflow: hidden` so the thumbnail and
// the row separators follow the rounded corners. A dropdown positioned
// `absolute` inside the card is clipped by that box and is effectively
// invisible. The menu therefore renders into `document.body` through a portal
// and positions itself `fixed`, from the trigger's viewport rect.
//
// Theme + token scope
// -------------------
// Every `--color-*` design token is scoped by
// `packages/excalidraw/css/theme.scss` to `.excalidraw` (+ `.theme--dark` for
// the dark palette), so a body-level portal would otherwise render tokenless.
// The portal container repeats those two classes, exactly like the Studio root
// does. It deliberately does NOT carry `.drawola-studio`, because
// `.excalidraw.drawola-studio` is the full-screen fixed overlay; it carries
// `.drawola-studio-portal` instead, which `studio.scss` reduces to a layout
// no-op (`display: contents`) while keeping the inherited tokens, font and
// the button specificity guard.
//
// The menu's own styles need no ancestor: `studio.scss` writes them with SCSS
// `&__menu` concatenation, i.e. the flat class selector `.drawola-studio__menu`
// which matches wherever the element lives.
//
// Events still travel the React tree (React portals bubble through the owning
// component tree), so the Studio root's `onKeyDown` — Escape closes, and it
// stops propagation to the editor's global shortcut handler — keeps working
// unchanged. The outside-click listener in DrawolaStudio.tsx is the one place
// that reads the DOM tree instead, so it matches `.drawola-studio-portal` too.

import { THEME } from "@excalidraw/common";
import clsx from "clsx";
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import type { Theme } from "@excalidraw/element/types";

/** vertical gap between the trigger and the menu, px */
const MENU_GAP = 4;
/** keep the menu at least this far from every viewport edge, px */
const VIEWPORT_MARGIN = 8;
/**
 * Only used when the menu cannot be measured yet (first layout pass, or a
 * zero-sized rect under jsdom). `min-width` is 11rem and the five items come
 * out around 190px tall.
 */
const ESTIMATED_MENU_WIDTH = 176;
const ESTIMATED_MENU_HEIGHT = 190;

export type MenuPosition = { top: number; left: number };

const clamp = (value: number, min: number, max: number) =>
  // `max` can fall below `min` when the menu is taller/wider than the
  // viewport; the near edge wins in that case.
  Math.min(Math.max(value, min), Math.max(min, max));

/**
 * Place the menu against the trigger, in viewport coordinates.
 *
 * - default: directly below the trigger, aligned to its end edge (right edge
 *   in LTR, left edge in RTL — the flow equivalent of the old
 *   `inset-inline-end: 0`)
 * - flip: above the trigger when there is not enough room below AND there is
 *   enough room above
 * - clamp: both axes are clamped into the viewport minus `VIEWPORT_MARGIN`
 */
export const computeMenuPosition = ({
  trigger,
  menuWidth,
  menuHeight,
  viewportWidth,
  viewportHeight,
  rtl = false,
}: {
  trigger: { top: number; bottom: number; left: number; right: number };
  menuWidth: number;
  menuHeight: number;
  viewportWidth: number;
  viewportHeight: number;
  rtl?: boolean;
}): MenuPosition => {
  const needed = menuHeight + MENU_GAP + VIEWPORT_MARGIN;
  const flip =
    viewportHeight - trigger.bottom < needed && trigger.top >= needed;

  const top = flip
    ? trigger.top - MENU_GAP - menuHeight
    : trigger.bottom + MENU_GAP;
  const left = rtl ? trigger.left : trigger.right - menuWidth;

  return {
    top: clamp(
      top,
      VIEWPORT_MARGIN,
      viewportHeight - menuHeight - VIEWPORT_MARGIN,
    ),
    left: clamp(
      left,
      VIEWPORT_MARGIN,
      viewportWidth - menuWidth - VIEWPORT_MARGIN,
    ),
  };
};

type Props = {
  /** Current app theme, so the portal matches the Studio root. */
  theme: Theme;
  open: boolean;
  /** Accessible name for both the trigger and the menu. */
  label: string;
  /** Trigger clicked — the owner flips its `openMenuId`. */
  onToggle: () => void;
  /** Close without acting. Must be referentially stable (`useCallback`). */
  onClose: () => void;
  /** The `role="menuitem"` buttons. */
  children: React.ReactNode;
};

export const StudioCardMenu: React.FC<Props> = ({
  theme,
  open,
  label,
  onToggle,
  onClose,
  children,
}) => {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const restoreFocusRef = useRef(false);
  const [position, setPosition] = useState<MenuPosition | null>(null);

  // Measure + place once per open. Until `position` lands the menu renders
  // hidden at the origin, which is what gives us its natural size to measure;
  // the state update happens inside a layout effect, so it is flushed before
  // the browser paints and there is no visible jump.
  useLayoutEffect(() => {
    if (!open) {
      setPosition(null);
      return;
    }
    const trigger = triggerRef.current;
    if (!trigger) {
      return;
    }
    const triggerRect = trigger.getBoundingClientRect();
    const menuRect = menuRef.current?.getBoundingClientRect();
    setPosition(
      computeMenuPosition({
        trigger: triggerRect,
        menuWidth: menuRect?.width || ESTIMATED_MENU_WIDTH,
        menuHeight: menuRect?.height || ESTIMATED_MENU_HEIGHT,
        viewportWidth: window.innerWidth,
        viewportHeight: window.innerHeight,
        rtl: window.getComputedStyle(trigger).direction === "rtl",
      }),
    );
  }, [open]);

  // A fixed menu cannot follow its trigger, so don't try: close instead.
  // `scroll` does not bubble, hence the capture-phase listener on `window` —
  // it catches the Studio's own scroller (`.drawola-studio__scroll`) as well
  // as any other scrolling ancestor.
  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const close = () => onClose();
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [open, onClose]);

  // Focus the first item once the menu is placed (a hidden element is not
  // focusable, so this waits for `position`). `position` is computed exactly
  // once per open, so this runs once too.
  useEffect(() => {
    if (!open || !position) {
      return;
    }
    const first =
      menuRef.current?.querySelector<HTMLElement>('[role="menuitem"]');
    if (first) {
      first.focus();
      restoreFocusRef.current = true;
    }
  }, [open, position]);

  // Closing removes the focused item from the DOM, which drops focus on
  // `<body>`. Hand it back to the trigger — but only then, so an action that
  // moves focus itself (rename opens an input) is left alone.
  useEffect(() => {
    if (open || !restoreFocusRef.current) {
      return;
    }
    restoreFocusRef.current = false;
    if (!document.activeElement || document.activeElement === document.body) {
      triggerRef.current?.focus();
    }
  }, [open]);

  return (
    <div className="drawola-studio__menu-wrap">
      <button
        ref={triggerRef}
        type="button"
        className="drawola-studio__menu-trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={label}
        title={label}
        onClick={(event) => {
          event.stopPropagation();
          onToggle();
        }}
      >
        ⋮
      </button>
      {open &&
        createPortal(
          <div
            className={clsx("excalidraw", "drawola-studio-portal", {
              "theme--dark": theme === THEME.DARK,
            })}
          >
            <div
              ref={menuRef}
              className="drawola-studio__menu"
              role="menu"
              aria-label={label}
              style={{
                top: position ? `${position.top}px` : 0,
                left: position ? `${position.left}px` : 0,
                visibility: position ? undefined : "hidden",
              }}
              onKeyDown={(event) => {
                // Escape also reaches the Studio root through the React tree
                // (portals bubble there); handling it here as well keeps the
                // menu closable even if the root handler ever moves.
                if (event.key === "Escape") {
                  onClose();
                }
              }}
            >
              {children}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};
